import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Loader2, ArrowLeft } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { AUTH } from '@/constants/testIds';
import Seo from '@/components/Seo';

export default function Login() {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const navigate = useNavigate();
  const { user, loading, login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user && user !== false) {
      navigate('/admin', { replace: true });
    }
  }, [user, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    const res = await login(email, password);
    setSubmitting(false);
    if (res.ok) {
      navigate('/admin', { replace: true });
    } else {
      setError(res.error);
    }
  };

  const onGoogle = () => {
    const redirectUrl = window.location.origin + '/admin';
    window.location.href =
      `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  if (loading) {
    return (
      <div className="section-light min-h-screen flex items-center justify-center bg-[var(--mw-bg)]">
        <Loader2 className="animate-spin text-white/40" />
      </div>
    );
  }

  return (
    <div
      data-testid={AUTH.loginPage}
      className="section-light min-h-screen flex items-center justify-center bg-[var(--mw-bg)] px-6"
    >
      <Seo title="Admin Sign In" path="/login" noindex />
      <div className="w-full max-w-md">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-white/55 hover:text-white transition-colors mb-10"
        >
          <ArrowLeft size={14} /> Back to site
        </Link>

        <div className="border border-white/10 bg-[var(--mw-surface)] p-8 sm:p-10">
          <div className="flex items-center gap-3 mb-3">
            <span className="block w-8 h-px bg-white/40" />
            <span className="text-[10px] uppercase tracking-[0.32em] text-white/55">
              Trade Console
            </span>
          </div>
          <h1 className="font-display font-light text-4xl text-white leading-[0.95] tracking-tight">
            Admin <em className="not-italic text-[var(--mw-primary)]">sign in</em>
          </h1>
          <p className="text-sm text-white/55 mt-3 leading-relaxed">
            Authorised personnel only. Use your Google account or seeded password.
          </p>

          <button
            type="button"
            onClick={onGoogle}
            data-testid={AUTH.googleButton}
            className="mt-8 w-full flex items-center justify-center gap-3 border border-white/15 hover:border-white text-white py-3 text-xs uppercase tracking-[0.22em] transition-colors"
          >
            <GoogleIcon />
            Continue with Google
          </button>

          <div className="flex items-center gap-3 my-7">
            <span className="flex-1 h-px bg-white/10" />
            <span className="text-[10px] uppercase tracking-[0.32em] text-white/40">
              or
            </span>
            <span className="flex-1 h-px bg-white/10" />
          </div>

          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label className="block text-[10px] uppercase tracking-[0.24em] text-white/55 mb-2">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="username"
                data-testid={AUTH.emailInput}
                className="w-full bg-transparent border border-white/15 focus:border-[var(--mw-primary)] outline-none px-4 py-3 text-sm text-white transition-colors"
              />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.24em] text-white/55 mb-2">
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                data-testid={AUTH.passwordInput}
                className="w-full bg-transparent border border-white/15 focus:border-[var(--mw-primary)] outline-none px-4 py-3 text-sm text-white transition-colors"
              />
            </div>

            {error && (
              <div
                data-testid={AUTH.errorMessage}
                className="border border-[var(--mw-primary)]/40 text-[var(--mw-primary)] text-xs uppercase tracking-[0.22em] px-4 py-3"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              data-testid={AUTH.submitButton}
              className="w-full bg-[var(--mw-primary)] hover:bg-[var(--mw-primary)]/90 disabled:opacity-60 text-white py-3 text-xs uppercase tracking-[0.22em] flex items-center justify-center gap-2 transition-colors"
            >
              {submitting && <Loader2 className="animate-spin" size={14} />}
              Sign in
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="#EA4335"
        d="M12 10.2v3.9h5.5c-.24 1.4-1.7 4.1-5.5 4.1-3.3 0-6-2.7-6-6s2.7-6 6-6c1.9 0 3.1.8 3.8 1.5l2.6-2.5C16.8 3.7 14.6 2.7 12 2.7 6.9 2.7 2.7 6.9 2.7 12s4.2 9.3 9.3 9.3c5.4 0 8.9-3.8 8.9-9.1 0-.6 0-1-.1-1.5H12z"
      />
    </svg>
  );
}
